import { Component, type ErrorInfo, type ReactNode } from 'react';
import { MicroLabel } from '@/ui/micro-label';

/**
 * A render crash used to blank the page silently, which made a missing API field look like a
 * broken build. This shows what actually failed instead.
 *
 * It sits inside the shells so navigation survives a screen-level crash: the sidebar or topbar
 * stays usable and only the failing screen is replaced.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[miraxis] screen crashed:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid place-items-center rounded border border-danger/40 bg-card px-6 py-16 text-center">
        <div className="grid max-w-[560px] justify-items-center gap-3">
          <MicroLabel size="xs" tone="danger">
            This screen failed to render
          </MicroLabel>
          <p className="font-mono text-[12px] break-words text-muted">{error.message}</p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-2 cursor-pointer rounded bg-raised px-3 py-1.5 text-[13px] text-foreground"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
